// Draws the Vega-Lite charts a public page inlines (the /docs/arena examples,
// ArenaDocsHelper#arena_docs_chart): each [data-vl-chart] container carries its
// spec in a <script type="application/json">, written like the arena report's
// block specs. Vega and the Vega-Lite compiler are import()ed into split chunks
// when a chart first nears the viewport, like the trends scatter's Vega, so a
// page without these charts never loads them. The chart fills its container's
// width and follows it on resize; the hover pill lives on the nearest
// [data-vl-tip-host] so a horizontally scrolling wrapper cannot clip it.
import { whenVisible } from "./lazy"
import { createTip } from "./tooltip"

export function initVegaLiteCharts() {
  document.querySelectorAll("[data-vl-chart]").forEach((el) => {
    whenVisible(el, () => mount(el).catch(() => {}))
  })
}

async function mount(el) {
  const script = el.querySelector("script[type='application/json']")
  if (!script) return

  const [vega, vegaLite] = await Promise.all([ import("vega"), import("vega-lite") ])
  const spec = JSON.parse(script.textContent)
  spec.width = el.clientWidth
  spec.autosize = { type: "fit-x", contains: "padding" }
  if (el.clientWidth < 520) narrowLegends(spec)

  const host = el.closest("[data-vl-tip-host]") || el
  host.style.position = "relative"
  let tip = null
  const tooltip = (view, event, item, value) => {
    if (!tip) return
    const text = tooltipText(value)
    if (!text) {
      tip.hide()
      return
    }
    const rect = host.getBoundingClientRect()
    tip.show(event.clientX - rect.left, event.clientY - rect.top, text)
  }

  // initialize() empties the container (the spec's script tag goes with it).
  const view = new vega.View(vega.parse(vegaLite.compile(spec).spec), { renderer: "svg", tooltip })
  view.initialize(el).hover()
  await view.runAsync()
  tip = createTip(host, { multiline: true })

  new ResizeObserver(() => {
    if (el.clientWidth && el.clientWidth !== view.width()) view.width(el.clientWidth).runAsync()
  }).observe(el)
}

// A phone-width chart: legends in two columns instead of one long row, or
// one when a label is long (the crowd names).
function narrowLegends(spec) {
  for (const unit of [ spec, ...(spec.layer || []) ]) {
    const color = unit.encoding?.color
    if (!color?.legend) continue
    const longest = Math.max(0, ...(color.scale?.domain || []).map((label) => String(label).length))
    color.legend.columns = longest > 16 ? 1 : 2
  }
}

// Vega-Lite hands a multi-field tooltip over as {title: formatted value};
// one line per field, empty fields left out.
function tooltipText(value) {
  if (value == null || value === "") return ""
  if (typeof value !== "object") return String(value)
  return Object.entries(value)
    .filter(([, v]) => v != null && v !== "")
    .map(([k, v]) => `${k}: ${v}`)
    .join("\n")
}
